import { useState } from "react";
import {
  BadgeCheck,
  Bell,
  Check,
  ChevronRight,
  Fingerprint,
  KeyRound,
  Laptop,
  Loader2,
  LockKeyhole,
  MapPin,
  ShieldCheck,
  Smartphone,
  TriangleAlert,
  UserRound,
} from "lucide-react";
import type { Me } from "@evocha/api-client";
import { api } from "../lib/api";
import { errorMessage } from "../lib/queries";
import { useSession } from "../lib/session";
import { initials, longDate } from "../lib/format";

type Tab = "business" | "security" | "notifications";

const TABS: Array<{ id: Tab; label: string; icon: typeof UserRound }> = [
  { id: "business", label: "Business profile", icon: UserRound },
  { id: "security", label: "Security", icon: ShieldCheck },
  { id: "notifications", label: "Notifications", icon: Bell },
];

const REGIONS = ["Dar es Salaam", "Arusha", "Mwanza", "Dodoma", "Mbeya", "Tanga", "Morogoro", "Kilimanjaro", "Geita", "Kigoma"];

const KYC_LABEL: Record<string, { label: string; tone: string }> = {
  approved: { label: "KYC approved", tone: "ev-badge-success" },
  submitted: { label: "KYC submitted", tone: "ev-badge-pending" },
  under_review: { label: "KYC in review", tone: "ev-badge-pending" },
  info_required: { label: "KYC needs info", tone: "ev-badge-pending" },
  rejected: { label: "KYC rejected", tone: "ev-badge-danger" },
  not_started: { label: "KYC not started", tone: "ev-badge-neutral" },
};

const NOTIFY_KEY = "evocha.agent.notifications";

function loadNotifications() {
  try {
    const raw = localStorage.getItem(NOTIFY_KEY);
    return raw ? (JSON.parse(raw) as Record<string, boolean>) : null;
  } catch {
    return null;
  }
}

export function Profile() {
  const { user, agent, updateAgent, signOut } = useSession();
  const [tab, setTab] = useState<Tab>("business");
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [business, setBusiness] = useState(agent?.business_name ?? "");
  const [region, setRegion] = useState(agent?.region ?? REGIONS[0]);
  const [district, setDistrict] = useState(agent?.district ?? "");

  const storedNotifications = loadNotifications();
  const [notifications, setNotifications] = useState({
    saleReceipt: storedNotifications?.saleReceipt ?? true,
    lowBalance: storedNotifications?.lowBalance ?? true,
    weeklySummary: storedNotifications?.weeklySummary ?? true,
    productNews: storedNotifications?.productNews ?? false,
    sms: storedNotifications?.sms ?? true,
    push: storedNotifications?.push ?? true,
  });

  const kyc = KYC_LABEL[agent?.kyc_status ?? ""] ?? { label: agent?.kyc_status ?? "KYC unknown", tone: "ev-badge-neutral" };

  function flash() {
    setSaved(true);
    window.setTimeout(() => setSaved(false), 3200);
  }

  async function saveProfile() {
    if (!business.trim() || busy) return;
    setBusy(true);
    setError(null);
    try {
      const me = await api.patch<Me>("/v1/me", {
        business_name: business.trim(),
        region,
        district: district.trim(),
      });
      updateAgent(me.agent);
      flash();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  function saveNotifications() {
    try {
      localStorage.setItem(NOTIFY_KEY, JSON.stringify(notifications));
    } catch {
      /* private mode — preferences apply for this session only */
    }
    flash();
  }

  function toggle(key: keyof typeof notifications) {
    setNotifications((n) => ({ ...n, [key]: !n[key] }));
  }

  return (
    <div className="stack-6">
      <section className="profile-hero">
        <div className="profile-identity">
          <span className="ev-avatar ev-avatar-lg profile-avatar">{initials(agent?.owner_name ?? agent?.business_name ?? "Agent")}</span>
          <div>
            <div className="ev-row ev-gap-2">
              <h2 className="ev-h2">{agent?.business_name ?? "Your business"}</h2>
              <span className={`ev-badge ${kyc.tone}`}><BadgeCheck size={12} /> {kyc.label}</span>
            </div>
            <p className="ev-muted">{agent?.owner_name ?? `${user?.first_name ?? ""} ${user?.last_name ?? ""}`.trim()} · {agent?.code ?? "—"} · {agent?.tier ?? "—"} tier</p>
          </div>
        </div>
        <div className="profile-meta">
          <div><span>Account status</span><strong className="ev-capitalize">{agent?.status?.toLowerCase() ?? "—"}</strong></div>
          <div><span>Region</span><strong>{agent?.region ?? "—"}</strong></div>
          <div><span>Commission</span><strong>{((agent?.commission_bps ?? 0) / 100).toFixed(1)}%</strong></div>
        </div>
      </section>

      <div className="profile-layout">
        <nav className="ev-card profile-nav" aria-label="Profile sections">
          {TABS.map(({ id, label, icon: Icon }) => (
            <button key={id} className={tab === id ? "profile-nav-item profile-nav-on" : "profile-nav-item"} onClick={() => setTab(id)}>
              <Icon size={17} />
              <span>{label}</span>
              <ChevronRight size={15} />
            </button>
          ))}
          <div className="profile-nav-foot">
            <LockKeyhole size={15} />
            <p>Your details are protected. evocha staff will never ask for your PIN.</p>
          </div>
        </nav>

        <div className="stack-5">
          {tab === "business" && (
            <>
              <section className="ev-card ev-card-pad">
                <div className="section-head">
                  <div>
                    <span className="ev-eyebrow">Business details</span>
                    <h3 className="ev-h3" style={{ marginTop: 4 }}>How you appear to evocha</h3>
                  </div>
                  <span className={`ev-badge ${agent?.status === "active" ? "ev-badge-success" : "ev-badge-pending"}`}>
                    {agent?.status ? agent.status.toLowerCase() : "—"}
                  </span>
                </div>

                <div className="form-grid">
                  <label className="ev-field">
                    <span className="ev-label">Business name</span>
                    <input className="ev-input" value={business} onChange={(e) => setBusiness(e.target.value)} maxLength={200} />
                  </label>
                  <label className="ev-field">
                    <span className="ev-label">Owner / operator</span>
                    <input className="ev-input" value={agent?.owner_name ?? ""} readOnly />
                  </label>
                  <label className="ev-field">
                    <span className="ev-label">Mobile number (sign-in)</span>
                    <div className="ev-input-affix">
                      <span className="ev-affix ev-affix-left">+255</span>
                      <input className="ev-input" value={agent?.phone ? agent.phone.replace(/^255/, "") : "managed by support"} readOnly />
                    </div>
                  </label>
                  <label className="ev-field">
                    <span className="ev-label">Region</span>
                    <select className="ev-select" value={region} onChange={(e) => setRegion(e.target.value)}>
                      {REGIONS.map((r) => <option key={r}>{r}</option>)}
                    </select>
                  </label>
                  <label className="ev-field">
                    <span className="ev-label">District / ward</span>
                    <input className="ev-input" value={district} onChange={(e) => setDistrict(e.target.value)} maxLength={100} />
                  </label>
                  <label className="ev-field">
                    <span className="ev-label">Agent code</span>
                    <input className="ev-input ev-mono" value={agent?.code ?? ""} readOnly />
                  </label>
                </div>

                {error && (
                  <div className="inline-success" role="alert" style={{ marginTop: 14, background: "var(--ev-red-100)", color: "var(--ev-red-600)" }}>
                    <TriangleAlert size={15} /> <span>{error}</span>
                  </div>
                )}

                <div className="section-actions">
                  <button
                    className="ev-btn ev-btn-sm ev-btn-ghost"
                    onClick={() => { setBusiness(agent?.business_name ?? ""); setRegion(agent?.region ?? REGIONS[0]); setDistrict(agent?.district ?? ""); setError(null); }}
                  >
                    Reset
                  </button>
                  <button className="ev-btn ev-btn-sm ev-btn-primary" onClick={() => void saveProfile()} disabled={busy || !business.trim()}>
                    {busy ? <><Loader2 size={14} className="spin" /> Saving…</> : "Save changes"}
                  </button>
                </div>
              </section>

              <section className="ev-card ev-card-pad">
                <div className="section-head">
                  <div>
                    <span className="ev-eyebrow">Verification</span>
                    <h3 className="ev-h3" style={{ marginTop: 4 }}>Compliance status</h3>
                  </div>
                  <span className={`ev-badge ${kyc.tone}`}>{kyc.label}</span>
                </div>
                <ul className="doc-list">
                  <li><span className="doc-icon"><BadgeCheck size={17} /></span><div><strong>KYC review</strong><span>Handled by evocha compliance</span></div><span className={`ev-badge ${kyc.tone}`}>{agent?.kyc_status?.replace("_", " ").toLowerCase() ?? "—"}</span></li>
                  <li><span className="doc-icon"><MapPin size={17} /></span><div><strong>Region</strong><span>{agent?.region ?? "—"}{agent?.district ? ` · ${agent.district}` : ""}</span></div><span className="ev-badge ev-badge-neutral">{agent?.tier ?? "—"} tier</span></li>
                  <li><span className="doc-icon"><Smartphone size={17} /></span><div><strong>Mobile money number</strong><span>{agent?.phone ? `+${agent.phone}` : "Managed by support"}</span></div><span className="ev-badge ev-badge-neutral">Sign-in</span></li>
                </ul>
                <p className="ev-hint">Need to change a legal document or your phone number? Contact support — changes are reviewed by compliance.</p>
              </section>
            </>
          )}

          {tab === "security" && (
            <>
              <section className="ev-card ev-card-pad">
                <div className="section-head">
                  <div>
                    <span className="ev-eyebrow">Sign in</span>
                    <h3 className="ev-h3" style={{ marginTop: 4 }}>Protect your account</h3>
                  </div>
                  <span className={`ev-badge ${user?.mfa_enabled ? "ev-badge-success" : "ev-badge-pending"}`}>
                    {user?.mfa_enabled ? "2FA on" : "2FA off"}
                  </span>
                </div>

                <div className="security-list">
                  <div className="security-row">
                    <span className="doc-icon"><KeyRound size={17} /></span>
                    <div><strong>Transaction PIN</strong><span>Required when you sign in to a new device.</span></div>
                    <button className="ev-btn ev-btn-sm ev-btn-secondary" onClick={() => setTab("business")}>Contact support</button>
                  </div>
                  <div className="security-row">
                    <span className="doc-icon"><Fingerprint size={17} /></span>
                    <div><strong>Two-factor authentication</strong><span>{user?.mfa_enabled ? "Enabled for this account." : "Add an authenticator app for sign-in."}</span></div>
                    <button className="ev-btn ev-btn-sm ev-btn-primary" disabled={user?.mfa_enabled}>Enable 2FA</button>
                  </div>
                  <div className="security-row">
                    <span className="doc-icon"><Smartphone size={17} /></span>
                    <div><strong>Biometric approval</strong><span>Approve sales with your fingerprint in the mobile app.</span></div>
                    <span className="ev-badge ev-badge-neutral">Mobile</span>
                  </div>
                </div>
              </section>

              <section className="ev-card ev-card-pad">
                <span className="ev-eyebrow">Active sessions</span>
                <h3 className="ev-h3" style={{ marginTop: 4 }}>Where you are signed in</h3>
                <div className="security-list">
                  <div className="security-row">
                    <span className="doc-icon"><Laptop size={17} /></span>
                    <div><strong>This browser</strong><span>{longDate()} · current session</span></div>
                    <span className="ev-badge ev-badge-success">This device</span>
                  </div>
                </div>
                <button className="ev-btn ev-btn-sm ev-btn-secondary" style={{ marginTop: 12 }} onClick={signOut}>Sign out everywhere</button>
                <p className="ev-hint">If you see a device you do not recognise, sign out and contact support immediately.</p>
              </section>
            </>
          )}

          {tab === "notifications" && (
            <section className="ev-card ev-card-pad">
              <div className="section-head">
                <div>
                  <span className="ev-eyebrow">Notifications</span>
                  <h3 className="ev-h3" style={{ marginTop: 4 }}>Choose what reaches you</h3>
                </div>
                <span className="ev-badge ev-badge-neutral">This browser</span>
              </div>

              <div className="notify-group">
                <span className="ev-label">Transactions</span>
                <Toggle label="Sale receipts" hint="Every completed or failed sale" value={notifications.saleReceipt} onChange={() => toggle("saleReceipt")} />
                <Toggle label="Low balance alerts" hint="When your available balance runs low" value={notifications.lowBalance} onChange={() => toggle("lowBalance")} />
                <Toggle label="Weekly summary" hint="Monday morning performance email" value={notifications.weeklySummary} onChange={() => toggle("weeklySummary")} />
              </div>

              <div className="notify-group">
                <span className="ev-label">Channels</span>
                <Toggle label="SMS" hint={agent?.phone ? `Sent to +${agent.phone}` : "Sent to your sign-in number"} value={notifications.sms} onChange={() => toggle("sms")} />
                <Toggle label="Push notifications" hint="On the evocha mobile app" value={notifications.push} onChange={() => toggle("push")} />
                <Toggle label="Product news" hint="New features and offers from evocha" value={notifications.productNews} onChange={() => toggle("productNews")} />
              </div>

              <div className="section-actions">
                <span className="ev-hint">Preferences are stored on this device.</span>
                <button className="ev-btn ev-btn-sm ev-btn-primary" onClick={saveNotifications}>Save preferences</button>
              </div>
            </section>
          )}

          <p className="profile-foot">Signed in as {user?.first_name} {user?.last_name} · {user?.role} · {longDate()}</p>
        </div>
      </div>

      {saved && <div className="toast" role="status"><Check size={16} /> Changes saved.</div>}
    </div>
  );
}

function Toggle({ label, hint, value, onChange }: { label: string; hint: string; value: boolean; onChange: () => void }) {
  return (
    <div className="notify-row">
      <div><strong>{label}</strong><span>{hint}</span></div>
      <button className={value ? "toggle toggle-on" : "toggle"} role="switch" aria-checked={value} aria-label={label} onClick={onChange}>
        <span />
      </button>
    </div>
  );
}
